'use client'; // for server side rendering

import { Link, styled } from '@mui/material';
import { Fragment, type ReactNode } from 'react';

export type FooterVariant = 'full' | 'slimLeft' | 'slimCenter';
export type FooterColor = 'default' | 'filled';

interface FooterProps {
    variant?: FooterVariant;
    color?: FooterColor;
    linksGroup?: ReactNode[];
    className?: string;
    linksGroupClassName?: string;
    copyRightClassName?: string;
    children?: ReactNode;
}

interface StyledFooterProps {
    footerVariant: FooterVariant;
    footerColor: FooterColor;
}

const StyledFooter = styled('footer')<StyledFooterProps>(
    ({ theme, footerVariant, footerColor }) => ({
        padding: theme.spacing(3),
        backgroundColor:
            footerColor === 'filled'
                ? theme.palette.background.reversed
                : theme.palette.background.paper,
        color: footerColor === 'filled' ? theme.palette.text.reversed : theme.palette.text.primary,
        textAlign: footerVariant === 'slimCenter' ? 'center' : 'left',
        '& a': {
            color: 'inherit',
        },
    })
);

const LinksGroupGrid = styled('div')(({ theme }) => ({
    display: 'grid',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    gridTemplateColumns: '1fr',
    [theme.breakpoints.up('md')]: {
        gridTemplateColumns: 'repeat(2, 1fr)',
    },
    [theme.breakpoints.up('lg')]: {
        gridTemplateColumns: 'repeat(4, 1fr)',
    },
}));

export function FooterLinkSeparator() {
    return <> | </>;
}

export function PrivacyLink() {
    return (
        <Link color="secondary" href="https://allenai.org/privacy-policy">
            Privacy Policy
        </Link>
    );
}

export function TermsOfUseLink() {
    return (
        <Link color="secondary" href="https://allenai.org/terms">
            Terms of Use
        </Link>
    );
}

export function BusinessConductLink() {
    return (
        <Link color="secondary" href="https://allenai.org/business-code-of-conduct">
            Business Code of Conduct
        </Link>
    );
}

export function ResponsibleUseLink() {
    return (
        <Link color="secondary" href="https://allenai.org/responsible-use">
            Responsible Use
        </Link>
    );
}

export function AI2TermsAndConditionsAllLinks() {
    return (
        <>
            <PrivacyLink />
            <FooterLinkSeparator />
            <TermsOfUseLink />
            <FooterLinkSeparator />
            <BusinessConductLink />
        </>
    );
}

export function AI2CopyrightLink() {
    return (
        <>
            <Link color="secondary" href="https://allenai.org">
                © The Allen Institute for Artificial Intelligence
            </Link>{' '}
            - All Rights Reserved
        </>
    );
}

const defaultLinksGroup = [
    <PrivacyLink key="privacy" />,
    <TermsOfUseLink key="terms" />,
    <ResponsibleUseLink key="responsible" />,
];

export function Footer({
    variant = 'full',
    color = 'default',
    linksGroup,
    className,
    linksGroupClassName,
    copyRightClassName,
    children,
}: FooterProps) {
    const resolvedLinks = linksGroup ?? defaultLinksGroup;

    return (
        <StyledFooter className={className} footerVariant={variant} footerColor={color}>
            {children || (
                <>
                    {variant === 'full' && (
                        <LinksGroupGrid className={linksGroupClassName}>
                            {resolvedLinks.map((link, id) => (
                                <Fragment key={id}>{link}</Fragment>
                            ))}
                        </LinksGroupGrid>
                    )}
                    <div className={copyRightClassName}>
                        <AI2CopyrightLink />
                    </div>
                </>
            )}
        </StyledFooter>
    );
}
